/**
 * Defensive timestamp utilities.
 *
 * Kept in a dedicated file (no date-fns / date-fns-tz imports) so that
 * non-React code paths and unit tests don't have to load the heavier date
 * formatting libraries just to normalize a Firestore Timestamp.
 */

/**
 * Defensively convert any timestamp-shaped value to a Date.
 *
 * Handles every shape Firestore + React Query can hand us:
 * - Firestore Timestamp instances (with .toDate())
 * - Serialized Timestamps from cache hydration ({ seconds, nanoseconds })
 *   or admin SDK ({ _seconds, _nanoseconds })
 * - JavaScript Date instances
 * - ISO strings, epoch numbers
 *
 * Returns null for falsy or unparseable values rather than throwing.
 */
export function toDateSafe(value: unknown): Date | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.toDate === 'function') {
      try {
        const d = (obj.toDate as () => Date)();
        return d instanceof Date && Number.isFinite(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    }
    const seconds =
      typeof obj.seconds === 'number' ? obj.seconds
      : typeof obj._seconds === 'number' ? obj._seconds
      : null;
    if (seconds !== null) {
      const nanos =
        typeof obj.nanoseconds === 'number' ? obj.nanoseconds
        : typeof obj._nanoseconds === 'number' ? obj._nanoseconds
        : 0;
      const d = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
      return Number.isFinite(d.getTime()) ? d : null;
    }
  }
  return null;
}

/**
 * Defensively convert any timestamp-shaped value to milliseconds.
 * Returns null for unparseable input. See toDateSafe() for shapes handled.
 */
export function toMillisSafe(value: unknown): number | null {
  const d = toDateSafe(value);
  return d ? d.getTime() : null;
}
